import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCustomerChangeOrders } from "@/hooks/useCustomerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { FileText } from "lucide-react";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const CustomerChangeOrders = () => {
  const { data: changeOrders, isLoading } = useCustomerChangeOrders();
  const isMobile = useIsMobile();

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  const items = changeOrders ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Change Orders</h1>
        <p className="text-muted-foreground text-sm">{items.length} change order{items.length !== 1 ? "s" : ""}</p>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><FileText className="mx-auto h-10 w-10 mb-2 opacity-40" /><p>No change orders available</p></CardContent></Card>
      ) : isMobile ? (
        <div className="space-y-3">
          {items.map((co) => (
            <Card key={co.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{co.number}</span>
                  <Badge variant="outline" className="capitalize text-xs">{co.status}</Badge>
                </div>
                {co.description && (
                  <p className="text-sm text-muted-foreground truncate">{co.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">{fmt(co.amount)}</span>
                  <span className="text-xs text-muted-foreground">{co.created_at?.split("T")[0]}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CO #</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((co) => (
                  <TableRow key={co.id}>
                    <TableCell className="font-medium">{co.number}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">{co.description || "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(co.amount)}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{co.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{co.created_at?.split("T")[0]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerChangeOrders;
